import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, contractService } from '../services/api';
import { JobType, JobStatus } from '../types';
import type { ContractSummaryDto } from '../types';

const JOB_TYPE_LABELS: Record<number, string> = {
  [JobType.SameDayDelivery]:   'Same-Day',
  [JobType.ScheduledDelivery]: 'Scheduled',
  [JobType.RelayDelivery]:     'Relay',
  [JobType.VehicleRepair]:     'Repair',
  [JobType.Removal]:           'Removal',
  [JobType.FreightHaulage]:    'Haulage',
};

const JOB_STATUS_LABELS: Record<number, string> = {
  [JobStatus.ContractGenerated]: 'Contract Ready',
  [JobStatus.CarrierAccepted]:   'Carrier Accepted',
  [JobStatus.InProgress]:        'In Progress',
  [JobStatus.Paused]:            'Paused',
  [JobStatus.Completed]:         'Completed',
  [JobStatus.PodVerified]:       'POD Verified',
  [JobStatus.PaymentReleased]:   'Paid',
  [JobStatus.ReviewUnlocked]:    'Review Unlocked',
  [JobStatus.Cancelled]:         'Cancelled',
  [JobStatus.Rejected]:          'Rejected',
};

type SortOption = 'date_desc' | 'date_asc' | 'amount_desc' | 'amount_asc';

const SORT_LABELS: Record<SortOption, string> = {
  date_desc:   'Newest first',
  date_asc:    'Oldest first',
  amount_desc: 'Highest value first',
  amount_asc:  'Lowest value first',
};

export default function MyContractsPage() {
  const { getToken, account } = useAppAuth();

  const [contracts, setContracts] = useState<ContractSummaryDto[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<JobType | ''>('');
  const [sort, setSort]             = useState<SortOption>('date_desc');
  const [downloading, setDownloading] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!account) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const api = createApiClient(getToken);
      const data = await contractService(api).getMyContracts({
        jobType: typeFilter === '' ? undefined : typeFilter,
        sort,
      });
      setContracts(data);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load contracts');
    } finally {
      setLoading(false);
    }
  }, [account, getToken, typeFilter, sort]);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (contractId: string) => {
    setDownloading(contractId);
    try {
      const api = createApiClient(getToken);
      const blobUrl = await contractService(api).downloadPdf(contractId);
      window.open(blobUrl, '_blank');
    } catch (e: any) {
      alert(e.message ?? 'Failed to download contract PDF.');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div style={{ width: '100%', padding: '24px 16px' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <Link to="/my-dashboard" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>
          ← Dashboard
        </Link>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '8px 0 4px' }}>My Contracts</h1>
        <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>
          Every contract where you are the Customer or the Service Provider — visible only to you and the other party.
        </p>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value === '' ? '' : (Number(e.target.value) as JobType))}
          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, background: '#fff' }}
        >
          <option value="">All job types</option>
          {Object.entries(JOB_TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortOption)}
          style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, background: '#fff' }}
        >
          {Object.entries(SORT_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>Loading…</div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#dc2626' }}>⚠️ {error}</div>
      ) : contracts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <p style={{ fontSize: 32, margin: '0 0 8px' }}>📄</p>
          <p style={{ color: '#9ca3af', fontSize: 14 }}>No contracts yet. A contract is generated automatically once you accept or have a quote accepted.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {contracts.map((c) => (
            <div
              key={c.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                padding: '14px 16px', borderRadius: 10,
                border: '1px solid #e5e7eb', background: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}
            >
              <span style={{
                fontSize: 11, fontWeight: 600, padding: '3px 8px',
                borderRadius: 6, background: '#eff6ff', color: '#1d4ed8', flexShrink: 0,
              }}>
                {JOB_TYPE_LABELS[c.jobType] ?? 'Job'}
              </span>

              <div style={{ flex: 1, minWidth: 200 }}>
                <Link to={`/jobs/${c.jobId}`} style={{ fontSize: 14, fontWeight: 600, color: '#111827', textDecoration: 'none' }}>
                  {c.jobReference}
                </Link>
                <p style={{ fontSize: 12, color: '#6b7280', margin: '2px 0 0' }}>
                  With {c.otherPartyName}
                  {' · '}
                  Generated {new Date(c.generatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  {c.lastRegeneratedAt && ` · Updated ${new Date(c.lastRegeneratedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                </p>
              </div>

              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <p style={{ fontSize: 14, fontWeight: 700, color: '#111827', margin: 0 }}>£{c.agreedPrice.toFixed(2)}</p>
                <p style={{ fontSize: 11, color: '#9ca3af', margin: '2px 0 0' }}>Platform Fee £{c.platformFeeAmount.toFixed(2)}</p>
              </div>

              <span style={{
                fontSize: 11, fontWeight: 600, padding: '2px 8px',
                borderRadius: 12, background: '#f3f4f6', color: '#374151', flexShrink: 0,
              }}>
                {JOB_STATUS_LABELS[c.jobStatus] ?? String(c.jobStatus)}
              </span>

              <button
                onClick={() => handleDownload(c.id)}
                disabled={downloading === c.id}
                style={{
                  fontSize: 12, fontWeight: 600, padding: '6px 12px',
                  borderRadius: 8, border: '1px solid #2563eb', cursor: 'pointer',
                  background: downloading === c.id ? '#e5e7eb' : '#fff', color: '#2563eb',
                  flexShrink: 0,
                }}
              >
                {downloading === c.id ? 'Opening…' : 'Download PDF'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
